<template>
	<div :class="{'l-content-section': !alternativeHeaderActive, '-header': !alternativeHeaderActive}">
		<div
			class="jumbo-carousel"
			:class="{
				'l-container': alternativeHeaderActive,
				'l-container--full-width@mobile': alternativeHeaderActive,
				'alternative-header-is-active': alternativeHeaderActive,
				'has-header-sponsors': hasHeaderSponsors,
			}"
		>
			<div
				v-if="slides.length > 1"
				class="jumbo-carousel__dots"
				:class="{'has-live-video': hasLiveVideo}"
			>
				<template v-if="alternativeHeaderActive">
					<span
						v-for="(slide, i) in slides"
						:key="i"
						class="jumbo-carousel__dot-w"
						:class="{'is-active': slideIndex === i}"
						@click="changeSlide(i)"
					>
						<span class="jumbo-carousel__dot" />
					</span>
				</template>
				<div
					v-else
					class="l-container"
				>
					<div class="l-row">
						<div
							class="l-col l-col--12 l-col--11@sm"
							:class="'l-offset--1@md'"
						>
							<span
								v-for="(slide, i) in slides"
								:key="i"
								class="jumbo-carousel__dot-w"
								:class="{'is-active': slideIndex === i}"
								@click="changeSlide(i)"
							>
								<span class="jumbo-carousel__dot" />
							</span>
						</div>
					</div>
				</div>
			</div>
			<Carousel
				ref="carousel"
				:cells="mappedSlides"
				:wrap-around="true"
				:auto-play="true"
				:disable-carousel-card-overflow="true"
				:center-horizontally="alternativeHeaderActive"
				class="jumbo-carousel__slider"
				@change="handleChange"
			>
				<template #cell="{cell}">
					<component
						:is="cell.link && !cell.showLiveVideo ? 'a' : 'div'"
						class="jumbo-carousel__slide"
						:class="{
							'has-live-video': cell.showLiveVideo,
							'has-background-video': hasBackgroundVideo(cell),
						}"
						:style="{
							'--background-image-url': `url('${$Helpers.getScaledImageUrl(cell.backgroundImageUrl, 2000, { heightIsWidth: true })}')`,
							'--background-image-mobile-url': `url('${$Helpers.getScaledImageUrl(cell.backgroundImageMobileUrl || cell.backgroundImageUrl, 700, { heightIsWidth: true })}')`
						}"
						v-bind="getLinkProps(cell, true)"
					>
						<div
							v-if="hasBackgroundVideo(cell)"
							class="jumbo-carousel__background-video"
						>
							<Player
								:key="`background-video-${cell.id}-${getBackgroundVideoHlsUrl(cell) || 'no-hls'}`"
								:ref="`backgroundVideoPlayer-${cell.id}`"
								class="jumbo-carousel__background-video-player"
								:hls-url="getBackgroundVideoHlsUrl(cell)"
								:poster="getBackgroundVideoPoster(cell)"
								:show-controls="false"
								:auto-play="true"
								:muted="true"
								:pointer-events="false"
								:show-errors="false"
								:loading-spinner-enabled="false"
								:loop="true"
								@play="() => handleBackgroundVideoPlay(cell.id)"
							/>
						</div>
						<div :class="[$arenaConfig.isPhygitalFamily && alternativeHeaderActive ? '' : 'l-container']">
							<div :class="[$arenaConfig.isPhygitalFamily && alternativeHeaderActive ? '' : 'l-row']">
								<div
									:class="[
										$arenaConfig.isPhygitalFamily && alternativeHeaderActive ? '' : 'l-offset--1@md',
										$arenaConfig.isPhygitalFamily && alternativeHeaderActive ? '' : 'l-col l-col--12 l-col--11@sm'
									]"
								>
									<div class="jumbo-carousel__slide-content-w">
										<div class="jumbo-carousel__slide-content-main">
											<img
												v-if="cell.imageUrl"
												class="jumbo-carousel__logo"
												:src="$Helpers.getScaledImageUrl(cell.imageUrl, 220)"
											>
											<div class="jumbo-carousel__slide-pretitle-w">
												<BadgeLive
													v-if="cell.slideType === ArenaSlideType.EVENT && getEventStatus(cell) === EVENT_STATUS.LIVE"
													modifier="small"
													class="jumbo-carousel__slide-live-badge"
												/>
												<h4
													v-if="cell.slideType === ArenaSlideType.EVENT"
													class="jumbo-carousel__slide-pretitle"
												>
													{{ getTimeDescription(cell) }}
												</h4>
											</div>
											<SectionTitle
												:title="cell.title"
												:vertical-spacing="false"
												type="main"
												class="jumbo-carousel__slide-title"
											/>
											<h5
												v-if="cell.subtitle"
												class="jumbo-carousel__slide-subtitle"
											>
												{{ cell.subtitle }}
											</h5>
											<Button
												v-if="cell.link"
												class="jumbo-carousel__link"
												:text="getMoreText(cell)"
												theme="dark"
												v-bind="getLinkProps(cell)"
											/>
											<span
												v-if="cell.link"
												class="jumbo-carousel__mobile-link"
											>{{ getMoreText(cell) }}
												<Icon
													icon-name="right-arrow"
													size="sm"
												/>
											</span>
										</div>
										<a
											v-if="cell.showLiveVideo"
											class="jumbo-carousel__slide-content-video"
											v-bind="getLinkProps(cell, true)"
										>
											<LivePreviewPlayer
												:product-placement-enabled="cell.event.productPlacementEnabled"
												:autostop-muted-after-seconds="120"
												:autostop-after-seconds="900"
												:poster="cell.backgroundImageUrl"
												:event-id="cell.eventId"
											/>
										</a>
									</div>
								</div>
							</div>
						</div>
					</component>
				</template>
			</Carousel>
		</div>
		<slot name="after" />
	</div>
</template>

<script>
import Carousel from '@js/components/Carousel';
import SectionTitle from '@js/components/SectionTitle';
import LivePreviewPlayer from '@js/components/LivePreviewPlayer';
import Player from '@shared/components/Player';
import Button from '@shared/components/Button';
import Icon from '@shared/components/Icon';
import BadgeLive from '@shared/components/BadgeLive';
import { httpClient } from '@shared/js/api/httpClient';
import { ArenaSlideType } from '@shared/enums/ArenaSlideType';
import { EVENT_STATUS, getEventStatus } from '@shared/utils/eventUtils';
import { getDateTime, getRelativeTime } from '@shared/utils/timeUtils';

export default {
	name:       'JumbotronCarousel',
	components: { Carousel, SectionTitle, Button, Icon, BadgeLive, LivePreviewPlayer, Player },
	props:      {
		slides: {
			type:     Array,
			required: true,
		},
		hasHeaderSponsors: {
			type:    Boolean,
			default: false,
		},
		alternativeHeaderActive: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			slideIndex:                 0,
			ArenaSlideType,
			EVENT_STATUS,
			unixNow:                    Math.round(new Date().getTime() / 1000),
			backgroundVideoPlayoutData: {},
		};
	},
	computed: {
		hasLiveVideo() {
			return this.slides.some((slide) => slide.slideType === ArenaSlideType.EVENT && this.getEventStatus(slide) === EVENT_STATUS.LIVE && slide.showLiveVideo);
		},
		mappedSlides() {
			return this.slides.map((slide) => {
				const hasLiveVideo = slide.slideType === ArenaSlideType.EVENT && this.getEventStatus(slide) === EVENT_STATUS.LIVE && slide.showLiveVideo;
				return {
					...slide,
					showLiveVideo:   hasLiveVideo,
					pauseForSeconds: hasLiveVideo ? 16 : 0,
				};
			});
		},
	},
	watch: {
		slides: {
			handler() {
				this.syncAndFetchBackgroundVideoPlayouts();
			},
			immediate: true,
		},
	},
	methods: {
		getBackgroundVideoEventIds() {
			return [...new Set(this.slides
				.filter((slide) => slide.slideType === ArenaSlideType.STANDARD && slide.backgroundType === 'video' && !!slide.backgroundVideoEventId)
				.map((slide) => slide.backgroundVideoEventId))];
		},
		syncAndFetchBackgroundVideoPlayouts() {
			this.getBackgroundVideoEventIds().forEach((eventId) => this.fetchBackgroundVideoPlayoutUrl(eventId));
		},
		getBackgroundVideoPlayoutState(eventId) {
			return this.backgroundVideoPlayoutData[eventId] || { status: 'idle', hlsUrl: '' };
		},
		setBackgroundVideoPlayoutState(eventId, nextState) {
			const currentState = this.getBackgroundVideoPlayoutState(eventId);
			this.$set(this.backgroundVideoPlayoutData, eventId, { ...currentState, ...nextState });
		},
		getMoreText(slide) {
			if (slide.slideType === ArenaSlideType.EVENT) {
				return $I18n.trans('arena.watch_now');
			}

			return $I18n.trans('commons.show_more');
		},
		getEventStatus(slide) {
			return getEventStatus(slide.event.type, slide.event.startTimestamp, slide.event.endTimestamp, this.unixNow);
		},
		getTimeDescription(slide) {
			const status = this.getEventStatus(slide);
			let eventPreTitle = '';
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);
			const gameStartDate = new Date(slide.event.startTimestamp * 1000);
			const gameIsToday = gameStartDate.toLocaleDateString() === new Date().toLocaleDateString();
			const gameIsTomorrow = gameStartDate.toLocaleDateString() === tomorrow.toLocaleDateString();

			switch (status) {
			case EVENT_STATUS.UPCOMING:
				if (gameIsTomorrow) {
					eventPreTitle += `${this.$I18n.trans('commons.tomorrow')} · ${getDateTime(slide.event.startTimestamp, { withDate: false })}`;
				} else if (gameIsToday) {
					eventPreTitle += `${this.$I18n.trans('commons.today')} · ${getDateTime(slide.event.startTimestamp, { withDate: false })}`;
				} else {
					eventPreTitle += `${getDateTime(slide.event.startTimestamp, { withWeekdayPrefix: true, dateTimeSeparator: ' · ' })}`;
				}
				break;
			case EVENT_STATUS.LIVE:
				eventPreTitle += getRelativeTime(slide.event.endTimestamp, { useRemaining: true, unixNow: this.unixNow });
				break;
			default:
				eventPreTitle += getDateTime(slide.event.startTimestamp, { withWeekdayPrefix: true, withTime: false });
			}

			return eventPreTitle;
		},
		handleChange(index) {
			this.slideIndex = index;
		},
		changeSlide(index) {
			this.$refs.carousel.select(index);
		},
		hasBackgroundVideo(slide) {
			return slide.slideType === ArenaSlideType.STANDARD &&
				slide.backgroundType === 'video';
		},
		getBackgroundVideoHlsUrl(slide) {
			const eventId = slide.backgroundVideoEventId;
			return eventId ? this.getBackgroundVideoPlayoutState(eventId).hlsUrl : '';
		},
		getBackgroundVideoPoster(slide) {
			return slide.backgroundVideo?.posterImageUrl || slide.backgroundImageUrl || slide.backgroundImageMobileUrl || '';
		},
		async fetchBackgroundVideoPlayoutUrl(eventId) {
			if (!eventId) {
				return;
			}

			const playoutState = this.getBackgroundVideoPlayoutState(eventId);
			if (playoutState.status === 'loading' || playoutState.status === 'ready') {
				return;
			}
			this.setBackgroundVideoPlayoutState(eventId, { status: 'loading' });

			try {
				const response = await httpClient.getEventPlayout(eventId);
				const hlsUrl = response?.data?.hlsUrl || '';
				if (!hlsUrl) {
					throw new Error('missing_hls_url');
				}
				this.setBackgroundVideoPlayoutState(eventId, {
					status: 'ready',
					hlsUrl,
				});
			} catch (error) {
				const status = error?.response?.status || 'network_error';
				console.error(`JumbotronCarousel: failed to fetch playout for event ${eventId}`, { status });

				this.setBackgroundVideoPlayoutState(eventId, {
					status: 'error',
					hlsUrl: '',
				});
			}
		},
		handleBackgroundVideoPlay(slideId) {
			this.$nextTick(() => {
				const refName = `backgroundVideoPlayer-${slideId}`;
				const playerComponent = Array.isArray(this.$refs[refName]) ? this.$refs[refName][0] : this.$refs[refName];
				if (playerComponent?.mute) {
					playerComponent.mute();
				}
			});
		},
		getLinkProps(slide, hrefAttribute = false) {
			if (!slide.link) {
				return null;
			}

			const url = new URL(slide.link, window.location.origin);
			const isInternal = url.host === window.location.host;

			return {
				[hrefAttribute ? 'href' : 'link']: slide.link,
				target:                            isInternal ? '_self' : '_blank',
				rel:                               isInternal ? 'opener' : 'noopener',
			};
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.jumbo-carousel {
	position: relative;

	&.alternative-header-is-active {
		margin-bottom: $sp6;

		@include size(sm-up) {
			margin-top: $sp5;
		}
	}
}

.jumbo-carousel__slider {
	.jumbo-carousel.alternative-header-is-active & {
		@include size(sm-up) {
			border-radius: var(--t-border-radius-card--lg);
			overflow: hidden;
		}
	}

	// remove bottom border radius when alternative header is active and there are header sponsors
	.jumbo-carousel.alternative-header-is-active.has-header-sponsors & {
		@include size(sm-up) {
			border-radius: var(--t-border-radius-card--lg) var(--t-border-radius-card--lg) 0 0;
		}
	}
}

.jumbo-carousel__slide {
	display: flex;
	align-items: center;
	position: relative;
	height: 377px;
	width: 100%;
	background-size: cover;
	background-position: center;
	padding-bottom: $sp5;
	background-image: var(--background-image-mobile-url);

	&::before {
		content: '';
		display: block;
		position: absolute;
		left: 0;
		bottom: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(180deg, rgba(var(--color-rgb-black), 0.2) 0%, var(--color-black) 100%);
		opacity: 0.8;
		z-index: -1;

		@include size(sm-up) {
			background: linear-gradient(180deg, rgba(var(--color-rgb-black), 0) 0%, var(--color-black) 100%);
		}

		@include size(md-up) {
			height: 85%;
		}
	}

	&.has-live-video {
		background-image: none;
		padding-top: $sp5;
		background-color: var(--color-grey-800);

		body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			background-color: var(--color-grey-200);
		}

		.l-container {
			@include size(xxl-up) {
				max-width: 80vw;
			}
		}
	}

	&.has-background-video {
		background-image: none;
	}

	/* stylelint-disable */
	body.-t-phygital .jumbo-carousel.alternative-header-is-active &,
	body.-t-astana2026 .jumbo-carousel.alternative-header-is-active &,
	body.-t-phygital-plus .jumbo-carousel.alternative-header-is-active & {
		// special header for phygital, reduced height. No guarantees that text will fit.
		aspect-ratio: 7/4 !important;
		height: auto !important;
		padding-bottom: $sp3 !important;
		padding-left: ($grid-gutter-width--xs) !important;
		padding-right: ($grid-gutter-width--xs) !important;

		@include size(sm-up) {
			aspect-ratio: 24/11 !important;
			padding-left: $sp8 !important;
			padding-right: $sp8 !important;
		}

		@include size(md-up) {
			aspect-ratio: 24/7 !important;
			padding-bottom: $sp6 !important;
			padding-left: $sp8 !important;
			padding-right: $sp8 !important;
		}
	}
	/* stylelint-enable */

	@include size(sm-up) {
		background-image: var(--background-image-url);
	}

	@include size(md-up) {
		align-items: flex-end;
		padding-bottom: $sp9;
		height: 520px;
	}

	@supports (aspect-ratio: 4/3) {
		height: auto;
		aspect-ratio: 4/3;

		@include size(sm-up) {
			aspect-ratio: 16/9;
		}

		@include size(md-up) {
			height: auto;
			aspect-ratio: 16/7;
		}

		@include size(lg-up) {
			aspect-ratio: 36/13;
		}
	}
}

.jumbo-carousel__background-video {
	position: absolute;
	inset: 0;
	z-index: -2;
}

.jumbo-carousel__background-video-player {
	width: 100%;
	height: 100%;

	::v-deep .v2-player__player-w,
	::v-deep .v2-player__player,
	::v-deep .v2-player__video {
		width: 100%;
		height: 100%;
	}

	::v-deep .v2-player__video {
		object-fit: cover;
	}
}

.jumbo-carousel__slide-content-w {
	.jumbo-carousel__slide.has-live-video & {
		display: flex;
		justify-content: space-between;
		gap: $sp5;
	}

	@include size(sm-down) {
		padding-top: 7%;
	}
}

.jumbo-carousel__slide-content-main {
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	align-items: flex-start;
	flex-grow: 1;

	@include size(sm-down) {
		z-index: 1;
		pointer-events: none;
	}
}

.jumbo-carousel__slide-content-video {
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	flex-shrink: 0;

	@include size(xs-only) {
		::v-deep {
			.v2-player__video {
				height: 100%;
				object-fit: cover;
				aspect-ratio: 4 / 3;
			}
		}
	}

	@include size(sm-down) {
		position: absolute;
		left: 0;
		bottom: 0;
		width: 100%;
		z-index: 0;
		justify-content: flex-start;

		&::after {
			content: '';
			display: block;
			position: absolute;
			left: 0;
			bottom: 0;
			width: 100%;
			height: 100%;
			background: linear-gradient(180deg, rgba(var(--color-rgb-black), 0.2) 0%, var(--color-black) 100%);
			pointer-events: none;
		}
	}

	@include size(md-up) {
		flex-basis: 40%;
	}

	@include size(lg-up) {
		flex-basis: 50%;
	}
}

.jumbo-carousel__video-iframe {
	width: 100%;
	aspect-ratio: 16 / 9;
}

.jumbo-carousel__logo {
	max-width: 80px;
	max-height: 60px;
	margin-bottom: $sp3;

	@include size(sm-up) {
		max-width: 130px;
		max-height: 80px;
	}

	@include size(md-up) {
		max-width: 140px;
		max-height: 110px;
		margin-bottom: $sp4;
	}
}

.jumbo-carousel__slide-pretitle-w {
	display: flex;
	align-items: center;
	margin-bottom: $sp2;
	gap: $sp3;
	margin-left: 2px; // optical alignment

	&:empty {
		display: none;
	}
}

// .jumbo-carousel__slide-live-badge {}

.jumbo-carousel__slide-pretitle {
	@include font(primary, book, normal, fs-100);
}

.jumbo-carousel__slide-title {
	margin-bottom: $sp3;

	/* stylelint-disable */
	::v-deep {
		.section-title__text {
			@include size(xs-only) {
				font-size: 25px !important;
				font-weight: 600 !important;
			}

			body.-t-phygital &,
			body.-t-astana2026 &,
			body.-t-phygital-plus & {
				@include size(md-only) {
					font-size: 28px !important;
				}
			}
		}
	}
	/* stylelint-enable */
}

/* stylelint-disable */
body.-t-light .jumbo-carousel__slide-content-w {
	::v-deep {
		.jumbo-carousel__slide-content-main,
		.section-title__text {
			color: var(--color-white);
		}
	}
}
/* stylelint-enable */

.jumbo-carousel__slide-subtitle {
	@include font(primary, light, normal, fs-120);
	margin-bottom: $sp3;
	padding-right: 30%;
	margin-left: 1px; // optical alignment

	@include size(sm-up) {
		@include font(primary, light, normal, fs-150);
	}

	@include size(md-up) {
		margin-bottom: $sp5;
	}

	/* stylelint-disable */
	body.-t-phygital &,
	body.-t-astana2026 &,
	body.-t-phygital-plus & {
		@include size(md-only) {
			font-size: 20px !important;
		}
	}
	/* stylelint-enable */
}

.jumbo-carousel__dots {
	position: absolute;
	width: 100%;
	bottom: $sp4;
	z-index: 1;

	&.has-live-video {
		@include size(xxl-up) {
			display: none;
		}
	}

	@include size(md-up) {
		bottom: $sp7-5;
	}

	.jumbo-carousel.alternative-header-is-active & { // stylelint-disable-line
		left: $sp4-5;

		@include size(sm-up) {
			left: $sp7;
		}
		@include size(md-up) {
			bottom: $sp3;
			left: auto;
			right: $sp7;
			display: flex;
			justify-content: flex-end;
		}
	}
}

.jumbo-carousel__dot-w {
	display: inline-block;
	padding: $sp2 0;
	cursor: pointer;

	&:not(:last-child) {
		margin-right: $sp3;
	}

	@include size(md-up) {
		&:not(:last-child) {
			margin-right: $sp2;
		}
	}
}

.jumbo-carousel__dot {
	width: $sp3;
	height: $sp3;
	background-color: var(--color-grey-500);
	border-radius: 50%;
	display: inline-block;

	.jumbo-carousel__dot-w.is-active & {
		background-color: var(--color-grey-200);

		body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			background-color: var(--color-grey-800);
		}
	}

	@include size(md-up) {
		width: $sp5;
		height: $sp2;
		background-color: var(--color-grey-500);
		border-radius: $border-radius--sm;
	}
}

.jumbo-carousel__link {
	.jumbo-carousel__slide-title + & {
		margin-top: $sp4;
	}

	@include size(sm-down) {
		display: none;
	}
}

.jumbo-carousel__mobile-link {
	display: flex;
	align-items: center;
	gap: $sp2;
	position: absolute;
	right: $sp5;
	bottom: $sp4;

	@include size(sm-up) {
		bottom: $sp5;
	}

	@include size(md-up) {
		display: none;
	}
}
</style>
